import { inject, InjectionKey, provide, reactive } from "vue";
import { SidUserClient } from "@sid/sid_client";
import { BaseInfo, CommonAttrInfo, ManageInfo, MemberAgreementInfo, ShippingInfo, SiteInfo, UserBase } from "@/commons/interfaces";


/**
 * 連携ユーザ情報
 * <pre>
 * アンケートシステム画面で扱うユーザ情報（基本属性情報）を提供する
 * </pre>
 */
export class QuestionnaireUserItem {

    /** インジェクションキー */
    public static injectionKey: InjectionKey<QuestionnaireUserItem> = Symbol(QuestionnaireUserItem.name);

    /** 基本属性情報セッションストレージキー */
    public static storageKey = QuestionnaireUserItem.name;


    /** 基本属性情報 */
    public readonly userBase: UserBase = reactive(new UserBase());

    /**
     * インスタンスを作成
     */
    public static create() {
        return new QuestionnaireUserItem();
    }

    /**
     * インジェクション
     * <pre>
     * ※親のVueコンポーネントでprovide()されていない場合例外をスローする
     * </pre>
     * @param key
     */
    public static inject() {
        const info = inject(QuestionnaireUserItem.injectionKey);
        if (info) {
            return info;
        }
        throw new Error("not provided.");
    }

    /**
     * コンストラクタ
     * @param klass ユーザ情報管理クラスの型
     */
    public constructor() {
        this.loadStorage();
    }

    /**
     * ユーザ情報設定
     * <pre>
     * ユーザ情報管理クラスが持つユーザ情報へのプロキシ
     * </pre>
     * @param obj
     */
    public setUserBase(obj: UserBase) {
        this.userBase.identityId = obj.identityId;
        this.userBase.userName = obj.userName;
        this.userBase.baseInfo = Object.assign(new BaseInfo(), obj.baseInfo);
        this.userBase.shippingInfo = obj.shippingInfo?.map(inf => Object.assign(new ShippingInfo(), inf)) || [];
        this.userBase.siteInfo = obj.siteInfo?.map(inf => Object.assign(new SiteInfo(), inf)) || [];
        this.userBase.commonAttrInfo = Object.assign(new CommonAttrInfo(), obj.commonAttrInfo);
        this.userBase.memberAgreeInfo = Object.assign(new MemberAgreementInfo(), obj.memberAgreeInfo);
        this.userBase.manageInfo = Object.assign(new ManageInfo(), obj.manageInfo);
    }

    /**
     * ユーザ情報を取得格納
     * @param sidClient
     */
    public async fetchUserData(sidClient: SidUserClient) {
        const response = await sidClient.getUserAttr()
            .then(res => res.data)
            .catch(res => {
                if (res.response?.status === 404) {
                    return new UserBase();
                } else {
                    throw new Error("Failed to get information.");
                }
            });
        this.setUserBase(response);
    }

    /**
     * このインスタンスをprovide()する
     */
    public provide() {
        provide(QuestionnaireUserItem.injectionKey, this);
    }

    /**
     * 基本属性情報をセッションストレージから読み出す
     */
    public loadStorage() {
        if (sessionStorage[QuestionnaireUserItem.storageKey])
            this.setUserBase(JSON.parse(sessionStorage[QuestionnaireUserItem.storageKey]));
    }

    /**
     * 基本属性情報をセッションストレージに格納
     */
    public storeStorage() {
        sessionStorage[QuestionnaireUserItem.storageKey] = JSON.stringify(this.userBase);
    }

    /**
     * 基本属性情報のセッションストレージを削除
     */
    public removeStorage() {
        sessionStorage.removeItem(QuestionnaireUserItem.storageKey);
    }
}
